import { weekday, type LocalDate } from '../dates';
import { spendAmount, spendingIn, type DateRange, type LedgerEntryLike } from '../ledger/counting';
import { normalizeNote } from './notes';
import { eachDay, weekStartOf, type TimeGrain, type WeekStart } from './range';

/** Bucket key for "no category" / "no method". */
export const NO_VALUE_KEY = '__none__';

export type SpendDimension =
  'category' | 'day' | 'week' | 'month' | 'weekday' | 'source' | 'method' | 'note';

export interface SpendBucket {
  key: string;
  label: string | null; // only set for 'note': the latest original note text
  totalMinor: number;
  count: number;
  entryIds: string[];
}

export interface BreakdownOptions {
  weekStartsOn?: WeekStart;
}

export interface SpendingSummary {
  totalMinor: number;
  count: number;
  uncategorizedCount: number;
}

export interface DrillFilter {
  range: DateRange;
  dim?: SpendDimension;
  key?: string;
  weekStartsOn?: WeekStart;
}

export function timeKey(grain: TimeGrain, date: LocalDate, weekStartsOn: WeekStart = 1): string {
  if (grain === 'day') return date;
  if (grain === 'week') return weekStartOf(date, weekStartsOn);
  return date.slice(0, 7);
}

/** The bucket an entry falls in for a dimension; null means it is left out (a note dimension with no note). */
export function bucketKey(
  dim: SpendDimension,
  e: LedgerEntryLike,
  opts: BreakdownOptions = {},
): string | null {
  switch (dim) {
    case 'category':
      return e.categoryId ?? NO_VALUE_KEY;
    case 'day':
    case 'week':
    case 'month':
      return timeKey(dim, e.date, opts.weekStartsOn ?? 1);
    case 'weekday':
      return String(weekday(e.date));
    case 'source':
      return e.sourceModule;
    case 'method':
      return e.method ?? NO_VALUE_KEY;
    case 'note': {
      const n = normalizeNote(e.note);
      return n === '' ? null : n;
    }
  }
}

function newestFirst<T extends LedgerEntryLike>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

function emptyBucket(key: string): SpendBucket {
  return { key, label: null, totalMinor: 0, count: 0, entryIds: [] };
}

/** Counted entries in the range, grouped by a dimension, biggest total first. */
export function breakdown(
  entries: readonly LedgerEntryLike[],
  range: DateRange,
  dim: SpendDimension,
  opts: BreakdownOptions = {},
): SpendBucket[] {
  const buckets = new Map<string, SpendBucket>();
  for (const e of newestFirst(spendingIn(entries, range))) {
    const key = bucketKey(dim, e, opts);
    if (key === null) continue;
    let b = buckets.get(key);
    if (!b) {
      b = { ...emptyBucket(key), label: dim === 'note' ? (e.note?.trim() ?? null) : null };
      buckets.set(key, b);
    }
    b.totalMinor += spendAmount(e);
    b.count += 1;
    b.entryIds.push(e.id);
  }
  return [...buckets.values()].sort(
    (a, b) => b.totalMinor - a.totalMinor || a.key.localeCompare(b.key),
  );
}

export function byCategory(entries: readonly LedgerEntryLike[], range: DateRange): SpendBucket[] {
  return breakdown(entries, range, 'category');
}

export function bySource(entries: readonly LedgerEntryLike[], range: DateRange): SpendBucket[] {
  return breakdown(entries, range, 'source');
}

export function byMethod(entries: readonly LedgerEntryLike[], range: DateRange): SpendBucket[] {
  return breakdown(entries, range, 'method');
}

export function topNotes(
  entries: readonly LedgerEntryLike[],
  range: DateRange,
  limit = 5,
): SpendBucket[] {
  return breakdown(entries, range, 'note').slice(0, limit);
}

/** Chronological buckets covering the whole range, including empty ones. */
export function timeSeries(
  entries: readonly LedgerEntryLike[],
  range: DateRange,
  grain: TimeGrain,
  opts: BreakdownOptions = {},
): SpendBucket[] {
  const found = new Map(breakdown(entries, range, grain, opts).map((b) => [b.key, b]));
  const keys: string[] = [];
  for (const d of eachDay(range)) {
    const k = timeKey(grain, d, opts.weekStartsOn ?? 1);
    if (keys[keys.length - 1] !== k) keys.push(k);
  }
  return keys.map((k) => found.get(k) ?? emptyBucket(k));
}

export function byWeek(
  entries: readonly LedgerEntryLike[],
  range: DateRange,
  opts: BreakdownOptions = {},
): SpendBucket[] {
  return timeSeries(entries, range, 'week', opts);
}

/** Seven buckets keyed '0' (Sunday) to '6', starting at the week start (Monday by default). */
export function byDayOfWeek(
  entries: readonly LedgerEntryLike[],
  range: DateRange,
  opts: BreakdownOptions = {},
): SpendBucket[] {
  const ws = opts.weekStartsOn ?? 1;
  const found = new Map(breakdown(entries, range, 'weekday', opts).map((b) => [b.key, b]));
  return Array.from({ length: 7 }, (_, i) => {
    const k = String((ws + i) % 7);
    return found.get(k) ?? emptyBucket(k);
  });
}

export function summarizeSpending(
  entries: readonly LedgerEntryLike[],
  range: DateRange,
): SpendingSummary {
  const rows = spendingIn(entries, range);
  return {
    totalMinor: rows.reduce((s, e) => s + spendAmount(e), 0),
    count: rows.length,
    uncategorizedCount: rows.filter((e) => e.categoryId === null).length,
  };
}

/** The entries behind a number: same range, same bucket key. Newest first. */
export function drillDown<T extends LedgerEntryLike>(entries: readonly T[], f: DrillFilter): T[] {
  const rows = spendingIn(entries, f.range).filter(
    (e) => !f.dim || bucketKey(f.dim, e, { weekStartsOn: f.weekStartsOn }) === f.key,
  );
  return newestFirst(rows);
}
